/**
 * 管理后台 · 邮件自检（见方案 2B.3 B 类）。
 *
 * 为什么要有一个"点一下发测试邮件"的入口：
 * 邮件配置错误（Resend 域名未验证、API Key 过期、SMTP 授权码写错）
 * 往往只在真正要发信的时候才暴露 —— 而那一刻通常是"家人找回密码失败"。
 * 提前点一次，比事后翻日志便宜得多。
 *
 * 后端直接调用 lib/mail.ts 的同一个 mailer，因此它验证的就是真实发信链路。
 */
import { useState } from 'react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { AdminShell } from '../../components/layout/AdminShell';
import { ManualCommands } from '../../components/admin/AdminBits';
import { useAdminMailTest } from '../../lib/queries';
import { toErrorMessage } from '../../lib/api/error';

export function AdminMailTestPage() {
  const toast = useToast();
  const mailTest = useAdminMailTest();
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<{ provider: string; to: string; elapsedMs: number } | null>(null);

  const onSend = async () => {
    setResult(null);
    try {
      const data = await mailTest.mutateAsync({ email: email.trim(), note: note.trim() });
      setResult({ provider: data.provider, to: data.to, elapsedMs: data.elapsedMs });
      toast.showToast('测试邮件已交给发信服务商', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  return (
    <AdminShell
      title="邮件自检"
      description="用真实发信链路发一封测试邮件，验证 Resend / SMTP 配置是否可用。"
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="发送测试邮件" description="收件人随便填一个你能收到的邮箱即可" />
          <div className="space-y-4">
            <Input
              label="收件邮箱"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="备注（可选）"
              placeholder="例如：2026-05 换域名后自检"
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button
              loading={mailTest.isPending}
              disabled={!email.includes('@')}
              onClick={() => void onSend()}
            >
              发送测试邮件
            </Button>

            {result && (
              <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">
                已交给 <strong>{result.provider}</strong> 发送到 {result.to}，耗时 {result.elapsedMs} ms。
                请到收件箱（含垃圾邮件目录）确认。
              </div>
            )}

            {mailTest.isError && (
              <div role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">
                {toErrorMessage(mailTest.error)}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="排查提示" description="发不出去时按这个顺序看" />
          <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-600">
            <li>
              <strong>MAIL_ENABLED=false</strong> 时会直接报「邮件功能未启用」：
              这是降级模式（验证码只写进日志、注册走邀请码），不是故障。
            </li>
            <li>
              <strong>Resend</strong>：确认发信域名已验证、<code>MAIL_FROM</code> 用的是该域名下的地址、
              <code>RESEND_API_KEY</code> 未过期。
            </li>
            <li>
              <strong>SMTP</strong>：QQ/163 邮箱要用「授权码」而不是登录密码；
              端口 587 配 <code>SMTP_SECURE=false</code>，465 配 <code>true</code>。
            </li>
            <li>收件人如果是自己的域名，注意对方也可能把它判成垃圾邮件。</li>
          </ol>
          <ManualCommands
            commands={[
              '# 看发信相关日志（不要打印 .env）',
              'docker compose -f deploy/docker-compose.yml logs --tail=200 api | grep -i mail',
              '# 确认当前邮件配置是否被识别（只看非密钥字段）',
              'docker compose -f deploy/docker-compose.yml exec api printenv | grep -E "^MAIL_(ENABLED|PROVIDER|FROM)="',
            ]}
          />
        </Card>
      </div>
    </AdminShell>
  );
}
